import { ReactNode, useRef, useState } from "react"

interface Props {
  children: [ReactNode, ReactNode]
  defaultHeight: number
}

export const Resizer = ({ defaultHeight, children }: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(defaultHeight)

  const startDragging = useRef<() => void>()
  if (!startDragging.current) {
    startDragging.current = () => {
      const setClientHeight = (clientY: number) => {
        const wrapperElement = wrapperRef.current
        if (!wrapperElement) return

        // Calculate the height of the bottom div based on cursor position
        const wrapperHeight = wrapperElement.offsetHeight
        const wrapperTop = wrapperElement.offsetTop
        const wrapperOffset = clientY - wrapperTop
        const rawHeight = wrapperHeight - wrapperOffset
        // Calculate height as a percentage of parent
        let height = Math.round((rawHeight / wrapperHeight) * 100)

        // Block heights that would completely overlap top div
        if (height > 90) height = 90

        // Block heights that would hide bottom div
        if (height < 10) height = 10

        setHeight(height)
      }

      const handleMouseMove = (event: MouseEvent) =>
        setClientHeight(event.clientY)
      const handleTouchMove = (event: TouchEvent) =>
        setClientHeight(event.touches[0].clientY)
      const handleStop = () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("touchmove", handleTouchMove)
        document.removeEventListener("mouseup", handleStop)
        document.removeEventListener("touchend", handleStop)
      }

      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("touchmove", handleTouchMove)
      document.addEventListener("mouseup", handleStop, { once: true })
      document.addEventListener("touchend", handleStop, { once: true })
    }
  }

  return (
    <div className="h-full w-full" ref={wrapperRef}>
      {children[0] && (
        <div
          className="relative overflow-hidden"
          style={{
            height: `${children[1] ? 100 - height : 100}%`,
          }}
        >
          <div className="absolute h-full w-full">{children[0]}</div>
        </div>
      )}
      {children[1] && (
        <div
          className="relative overflow-hidden"
          style={{
            height: `${children[0] ? height : 100}%`,
          }}
        >
          <div className="absolute h-full w-full">
            <div
              className="z-1 block h-[6px] w-full cursor-row-resize bg-(--bg-primary) select-none motion-safe:hover:transition-all motion-safe:hover:duration-200 motion-safe:hover:ease-in-out"
              role="generic"
              onMouseDown={startDragging.current}
              onTouchStart={startDragging.current}
            />
            {children[1]}
          </div>
        </div>
      )}
    </div>
  )
}
